/** @jsx jsx */
import { jsx, Button } from "theme-ui";
import React, { useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import keycloak, { KEYCLOAK_SCOPE } from "keycloak";
import { useToasts } from "react-toast-notifications";
import { queryClient } from "api/queryClient";
import { t } from "@lingui/macro";
import { withI18n } from "@lingui/react";
import { I18n } from "@lingui/core";

// @ts-ignore
import useOnlineStatus from "@rehooks/online-status";

import { resetApp, useAppDispatch, useAppSelector } from "store/store";
import { isAuthenticated } from "../../store/authentication";
import { authoriseSuccess, countAuthoriseFailures, isAuthorisationDone, isAuthorised, MAX_RETRY_COUNT } from "../../store/authorisation";
import { AppInsightsContext } from "providers/AppinsightsProvider/AppInsightsProvider";
import { LICENSE_ID_STORAGE_KEY } from "config/constants";
import Offline from "routes/Offline/Offline";
import { getUser } from "api/api";
import { useQuery } from "react-query";

export interface AuthStateContext {
    companyId: string;
    token: string;
    offlineToken: string;
}

interface AuthenticationProviderProps {
    children: any;
    i18n: I18n;
}

interface AuthenticationContextProps {
    companyId?: string;
    licenseId?: string;
    isAuthInProgress: boolean;
    authenticated: boolean;
    authorised: boolean;
    isError: boolean;
    error?: string;
    disconnect: () => void;
}

const exponentialBackoffWithJitter = (
    attemptIndex: number,
    error: any,
    baseDelay: number = 1000,
    maxDelay: number = 60000
): number => {
    // Calculate the base exponential delay
    const exponentialDelay = Math.pow(2, attemptIndex) * baseDelay;

    // Cap the base delay at the maximum
    const cappedDelay = Math.min(exponentialDelay, maxDelay);

    // Calculate the "half delay" for jitter
    const halfDelay = cappedDelay / 2;

    // The final delay will be between halfDelay and cappedDelay
    // (e.g., 50% to 100% of the capped exponential delay)
    const finalDelay = halfDelay + Math.random() * halfDelay;

    return finalDelay;
};
export const AuthenticationContext = React.createContext<
    AuthenticationContextProps
>({
    authenticated: false,
    authorised: false,
    isError: false,
    licenseId: "",
    companyId: undefined,
    isAuthInProgress: false,
    error: '',
    disconnect: () => {
        /* intentional */
    },
});

export const useAuthentication = () => useContext(AuthenticationContext);
const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({
    children,
    i18n,
}) => {
    const history = useHistory();
    const { addToast, toastStack, removeAllToasts } = useToasts();
    const dispatch = useAppDispatch();
    const state = useAppSelector(s => s);
    const { authentication } = state;
    const authenticated = useAppSelector(isAuthenticated)
    const authorised = useAppSelector(isAuthorised)
    const companyId = useAppSelector(s => s.authorisation.entity?.companyId);
    const license = useAppSelector(s => s.license.entity)
    const authorisationFinished = useAppSelector(isAuthorisationDone);
    const appInsights = useContext(AppInsightsContext);
    const isOnline = useOnlineStatus();

    const queryRetryOptions = useMemo(() => ({
        retryDelay: exponentialBackoffWithJitter,
        retry: (failureCount: number, err: any) => {
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                dispatch(countAuthoriseFailures(err.response.data))
            }

            if (failureCount <= MAX_RETRY_COUNT) {
                return true;
            }
            return false;
        },
        onError: (err: any) => {
            if (appInsights) {
                appInsights.trackException({
                    exception: new Error(`queryClient.onError ${err?.response?.statusText} ${err?.response?.status}`),
                    properties: {
                        error: err,
                        state,
                        licenseId: localStorage.getItem(LICENSE_ID_STORAGE_KEY),
                    }
                });
            }

            if (err?.response?.status === 403 || err?.response?.status === 401) {
                addToast(err?.response?.statusText, { appearance: 'error', autoDismiss: true })
                renderMessageToUser();
            }
        },

    }), [appInsights])

    useQuery("user", () => getUser(), {
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            dispatch(authoriseSuccess(data))
        },
        enabled: authenticated,
        ...queryRetryOptions

    });

    const disconnect = () => {
        localStorage.clear();
        keycloak.logout();
    };

    useEffect(() => {
        if (history.location.pathname !== '/login' && !authentication.isLoading && !authenticated) {
            history.push('/login')
        } else if (!authorised && authorisationFinished && authenticated) {
            history.push('/license')
        } else if (authorised && authorisationFinished && authenticated && companyId) {
            history.push(`/${companyId}`)
        } else if (history.location.pathname === '/') {
            history.push('/auth');
        }
    }, [authorisationFinished, authorised, authenticated, history.location.pathname, companyId]);

    useEffect(() => {
        queryClient.setDefaultOptions({
            queries: {
                ...queryRetryOptions
            }
        })
    }, [appInsights])

    const renderMessageToUser = () => {
        const isRetriesExceeded = true;
        const textContent = i18n._(t("info.thisIsTakingTime")``);
        const buttonTextWarning = i18n._(t("info.retryNow")``);
        const buttonTextError = i18n._(t("info.reload")``);

        const message = (
            <div>
                <p>{textContent}</p>
                <div
                    style={{
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Button
                        onClick={() => {
                            queryClient.refetchQueries();
                            removeAllToasts();
                            dispatch(resetApp())
                            keycloak.login({ scope: KEYCLOAK_SCOPE })
                        }}
                    >
                        {!isRetriesExceeded ? buttonTextWarning : buttonTextError}
                    </Button>
                </div>
            </div>
        );

        if (toastStack.length === 0) {
            addToast(message, {
                appearance: "info",
            });
        }
    };

    useEffect(() => {
        if (
            authorised &&
            companyId &&
            !history.location.pathname.includes(companyId) &&
            !history.location.pathname.includes("state") &&
            history.location.pathname !== '/license'
        ) {
            history.push(`/${companyId}`);
        } else if (authenticated && license === null) {
            history.push('/license');
        }
    }, [authorised, authenticated, companyId, history.location.pathname]);



    if (!isOnline) {
        return <Offline />;
    }

    return (
        <AuthenticationContext.Provider
            value={{
                authenticated,
                authorised,
                companyId,
                isAuthInProgress: authentication.isLoading,
                disconnect,
                isError: authentication.isError,
            }}
        >
            {children}
        </AuthenticationContext.Provider>
    );
};

export default withI18n()(AuthenticationProvider);
